import { useState } from 'react'
import styled from 'styled-components'
import { CustomProvider, DateRangePicker } from 'rsuite'
const { afterToday } = DateRangePicker
import ptBR from 'rsuite/locales/pt_BR'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import {
  AutoCompleteWrapper,
  BarChartWrapper,
  ChartWithNoteWrapper,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
} from '../../components'
import useViewport from '../../hooks/useViewport'
import filterDrawerHelper from '../../utils/filterDrawer'
import { RequestService } from '../../services/RequestService'
import {
  SleepQuantityProvider,
  GRANULARITY,
  useSleepQuantity,
} from '../../contexts/SleepQuantityContext'
import { getHHmm } from '../../utils/dateConversion'
import { useTranslation } from "react-i18next"

const Container = styled(Box)`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
`

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #f1f3f5;
  padding: 24px 44px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ChartContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
`

const FiltersTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const customFormat = value => {
  const hours = Math.floor(value / 60)
  const minutes = value % 60

  return getHHmm({ hours, minutes })
}

const ReportSleepQuantityWrapper = () => {
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const {
    data,
    loading,
    selectedUser,
    onSelectUser,
    dateRange,
    onDateRangeChange,
    selectedFilterOptions,
    filterOptions,
    loadingFilters,
    onSelectFilter,
    inputValue,
    onInputValueChange,
    granularity,
    onChangeGranularity,
    graphMessage,
  } = useSleepQuantity()
  const { width } = useViewport()
  const { t } = useTranslation('common')
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper

  return (
    <Container>
      <Body>
        <Title>
          <span style={{ fontSize: 20, fontWeight: 600 }}>
            {t('reports.sleep_quantity.title')}
          </span>
          <Tooltip
            title={t('reports.sleep_quantity.tooltip')}
            placement='right'
            arrow
          >
            <HelpOutline fontSize='24' />
          </Tooltip>
        </Title>
        <ChartContainer>
          <FiltersTagsContainer>
            <FiltersContainer>
              <div style={{ display: 'flex', flex: 1 }}>
                <AutoCompleteWrapper
                  onLoad={() => RequestService.getUsers({ visibility: 'reports' })}
                  value={selectedUser}
                  onChangeValue={onSelectUser}
                  inputValue={inputValue}
                  onInputValueChange={onInputValueChange}
                />
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    gap: 16,
                  }}
                >
                  <CustomProvider locale={ptBR}>
                    <DateRangePicker
                      defaultCalendarValue={dateRange}
                      value={dateRange}
                      onChange={event => onDateRangeChange(event)}
                      showOneCalendar
                      shouldDisableDate={afterToday()}
                      character=' - '
                      placement='bottom'
                      placeholder={t('reports.sleep_quantity.placeholder')}
                      ranges={[]}
                      cleanable={false}
                    />
                  </CustomProvider>
                  <FormControl
                    style={{ display: 'flex', flex: 1, minWidth: 120 }}
                  >
                    <InputLabel id='group-by'>Agrupar por</InputLabel>
                    <Select
                      labelId='group-by-label'
                      id='group-by'
                      label={t('reports.sleep_quantity.group_by')}
                      value={granularity}
                      onChange={onChangeGranularity}
                      size='small'
                    >
                      <MenuItem value={GRANULARITY.TITLE}>
                        {t('reports.sleep_quantity.menu.role')}
                      </MenuItem>
                      <MenuItem value={GRANULARITY.SHIFT}>{t('reports.sleep_quantity.menu.shift')}</MenuItem>
                      <MenuItem value={GRANULARITY.TEAM}>{t('reports.sleep_quantity.menu.team')}</MenuItem>
                    </Select>
                  </FormControl>
                  <Filter onClick={() => setOpenFilterDrawer(true)} />
                </div>
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            })?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <GroupedFilterTags
                  tags={selectedFilterOptionsList({
                    selectedFilterOptions,
                  })}
                  onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                  maxTagsToShow={getMaxTagsToShow({ width })}
                />
              </div>
            )}
          </FiltersTagsContainer>

          <ChartWithNoteWrapper
            customMessage={
              <>
                {t('calulation')} <b>{graphMessage?.totalUsers} {t('users')}</b>{' '}
                {t('totaling')} <b>{graphMessage?.totalHours} </b> {t('reports.sleep_quantity.hours_slept')}
              </>
            }
          >
            {!loading && (
              <BarChartWrapper
                data={data}
                categories={[]}
                colors={['#E27837']}
                stacked={false}
                formatter={val => customFormat(val)}
              />
            )}
          </ChartWithNoteWrapper>
        </ChartContainer>
      </Body>

      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={onSelectFilter}
        loading={loadingFilters}
        options={filterOptions}
        withShift
        withPosition
        withTeam
      />
    </Container>
  )
}

const ReportSleepQuantity = () => {
  return (
    <SleepQuantityProvider>
      <ReportSleepQuantityWrapper />
    </SleepQuantityProvider>
  )
}

export default ReportSleepQuantity
